/**
 * Resolves which rate applied to an employee on a given date.
 *
 * get_payroll_rate_context returns assignment windows already intersected with
 * band rate windows, so a lookup is a matter of finding the segment covering
 * the date and filling any premium the band left null from company settings.
 *
 * Three answers, most specific first: the pay band the employee is assigned
 * to, then the default rate of the guard rank they hold, then the company
 * base rate. Only the last is a fallback somebody needs to put right.
 */

import type { PayrollRateSegment, PayrollSettings, ResolvedRate } from './types';

export const UNBANDED_CODE = 'UNBANDED';
export const RANK_DEFAULT_CODE = 'RANK';

export type RateResolver = (guardId: string, dateKey: string) => ResolvedRate;

/** The default hourly rate of the guard rank an employee holds. */
export interface RankPayDefault {
  guardId: string;
  roleName: string;
  hourlyRate: number;
}

function coalesce(value: number | null | undefined, fallback: number): number {
  return value === null || value === undefined || !Number.isFinite(value) ? fallback : value;
}

/**
 * Fallback for an employee with no pay assignment covering the date. Uses the
 * company base rate so an export still balances, and marks the row so the UI
 * can prompt the admin to assign a band.
 */
function buildFallbackRate(settings: PayrollSettings): ResolvedRate {
  return {
    payBandId: null,
    payBandCode: UNBANDED_CODE,
    payBandName: 'Unbanded',
    baseHourlyRate: settings.baseHourlyRateEur,
    isRateOverride: false,
    isFallback: true,
    employmentType: null,
    payrollReference: null,
    costCentre: null,
    annualLeaveAccrualPercent: settings.annualLeaveAccrualPercent,
    sundayPremiumMultiplier: settings.sundayPremiumMultiplier,
    publicHolidayMultiplier: settings.publicHolidayBenefitMultiplier,
    unsocialShiftPremium: settings.unsocialHoursShiftPremiumEur,
    overtimeDailyMultiplier: settings.overtimeDailyMultiplier,
    overtimeWeeklyMultiplier: settings.overtimeWeeklyMultiplier,
    doubleTimeMultiplier: settings.doubleTimeMultiplier,
    minimumShiftPayMinutes: settings.minimumShiftPayMinutes,
  };
}

/**
 * An employee with no pay band, paid at the rate their rank carries. The
 * company chose that rate for the rank, so this is an answer rather than a
 * gap: it is not flagged, and premiums come from company settings as they do
 * for any band that leaves them unset.
 *
 * A rank's rate is not effective-dated. It applies to any day no band covers,
 * which is why a period is locked before it is paid.
 */
function buildRankRate(rank: RankPayDefault, settings: PayrollSettings): ResolvedRate {
  return {
    ...buildFallbackRate(settings),
    payBandCode: RANK_DEFAULT_CODE,
    payBandName: rank.roleName,
    baseHourlyRate: rank.hourlyRate,
    isFallback: false,
  };
}

function resolveSegment(segment: PayrollRateSegment, settings: PayrollSettings): ResolvedRate {
  return {
    payBandId: segment.payBandId,
    payBandCode: segment.payBandCode,
    payBandName: segment.payBandName,
    baseHourlyRate: segment.baseHourlyRate,
    isRateOverride: segment.isRateOverride,
    isFallback: false,
    employmentType: segment.employmentType,
    payrollReference: segment.payrollReference,
    costCentre: segment.costCentre,
    annualLeaveAccrualPercent: coalesce(
      segment.annualLeaveAccrualPercent,
      settings.annualLeaveAccrualPercent,
    ),
    sundayPremiumMultiplier: coalesce(segment.sundayPremiumMultiplier, settings.sundayPremiumMultiplier),
    publicHolidayMultiplier: coalesce(
      segment.publicHolidayMultiplier,
      settings.publicHolidayBenefitMultiplier,
    ),
    unsocialShiftPremium: coalesce(segment.unsocialShiftPremium, settings.unsocialHoursShiftPremiumEur),
    overtimeDailyMultiplier: coalesce(segment.overtimeDailyMultiplier, settings.overtimeDailyMultiplier),
    overtimeWeeklyMultiplier: coalesce(segment.overtimeWeeklyMultiplier, settings.overtimeWeeklyMultiplier),
    doubleTimeMultiplier: coalesce(segment.doubleTimeMultiplier, settings.doubleTimeMultiplier),
    minimumShiftPayMinutes: coalesce(segment.minimumShiftPayMinutes, settings.minimumShiftPayMinutes),
  };
}

export function buildRateResolver(
  segments: PayrollRateSegment[],
  settings: PayrollSettings,
  rankDefaults: RankPayDefault[] = [],
): RateResolver {
  const byGuard = new Map<string, PayrollRateSegment[]>();

  segments.forEach((segment) => {
    if (!byGuard.has(segment.guardId)) byGuard.set(segment.guardId, []);
    byGuard.get(segment.guardId)!.push(segment);
  });

  // Later segments first, so the most recent applicable window wins when
  // ranges touch at a boundary.
  byGuard.forEach((list) => list.sort((left, right) => (
    right.segmentStart.localeCompare(left.segmentStart)
  )));

  const fallback = buildFallbackRate(settings);
  const rankRates = new Map<string, ResolvedRate>();
  rankDefaults.forEach((rank) => {
    if (Number.isFinite(rank.hourlyRate) && rank.hourlyRate >= 0) {
      rankRates.set(rank.guardId, buildRankRate(rank, settings));
    }
  });
  const cache = new Map<string, ResolvedRate>();

  return (guardId: string, dateKey: string): ResolvedRate => {
    const cacheKey = `${guardId}|${dateKey}`;
    const cached = cache.get(cacheKey);
    if (cached) return cached;

    const candidates = byGuard.get(guardId) ?? [];
    const match = candidates.find((segment) => (
      segment.segmentStart <= dateKey && dateKey <= segment.segmentEnd
    ));

    const resolved = match
      ? resolveSegment(match, settings)
      : rankRates.get(guardId) ?? fallback;
    cache.set(cacheKey, resolved);
    return resolved;
  };
}

/** Employees in the period with no band assignment covering their work. */
export function countUnbandedEmployees(resolvedByEmployee: Map<string, ResolvedRate>): number {
  let count = 0;
  resolvedByEmployee.forEach((rate) => {
    if (rate.isFallback) count += 1;
  });
  return count;
}
